import React, { useState, useContext } from 'react';
import './Login.css';
import LoadingContext from '../contexts/LoadingContext';
import { fetchWIPDetails, initializeSession } from '../utils/api';

const Login = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  
  // Use global loading context
  const { setLoading } = useContext(LoadingContext);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Use full URL for cross-origin requests
  const FRAPPE_URL = process.env.REACT_APP_FRAPPE_URL || 'http://pra.localhost:8000';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!credentials.username || !credentials.password) {
      setError('Please enter both username and password');
      return;
    }

    setLoading(true);
    setIsLoading(true);
    setError('');

    console.log('Attempting login with username:', credentials.username.trim());

    try {
      // Password-based authentication using Frappe REST API
      const loginResponse = await fetch(`${FRAPPE_URL}/api/method/login`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important: Include cookies for session management
        body: JSON.stringify({
          usr: credentials.username.trim(),
          pwd: credentials.password
        })
      });

      if (!loginResponse.ok) {
        const errorData = await loginResponse.json().catch(() => ({}));
        throw new Error(errorData.message || 'Invalid username or password');
      }

      // Log response headers to check for Set-Cookie
      console.log('Login Response Status:', loginResponse.status);
      console.log('Login Response Headers:');
      loginResponse.headers.forEach((value, key) => {
        console.log(`  ${key}: ${value}`);
      });
      
      // Extract sid from Set-Cookie header (though browser may hide it)
      const setCookieHeader = loginResponse.headers.get('set-cookie');
      console.log('Set-Cookie header:', setCookieHeader);
      
      // Check cookies in document
      console.log('Document cookies after login:', document.cookie);

      const loginData = await loginResponse.json();
      console.log('Login successful:', loginData);
      
      // Store user info in localStorage
      localStorage.setItem('frappe_logged_in', 'true');
      localStorage.setItem('frappe_user', JSON.stringify({
        username: credentials.username.trim(),
        full_name: loginData.full_name || credentials.username.trim(),
        home_page: loginData.home_page || '/app'
      }));

      // Wait a moment for cookies to be established
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Check cookies again after waiting
      console.log('Document cookies after wait:', document.cookie);
      
      // Initialize session and get CSRF token
      try {
        const sessionResult = await initializeSession();
        if (sessionResult.success) {
          console.log('✓ Session initialized successfully');
        } else {
          console.warn('⚠️ Session initialization had issues:', sessionResult.error);
        }
      } catch (sessionErr) {
        console.warn('⚠️ Session initialization failed (non-blocking):', sessionErr.message);
      }
      
      // Fetch WIP details using the shared utility (backend returns line in response)
      try {
        const result = await fetchWIPDetails();
        if (result.success && result.line) {
          console.log('✓ User line number:', result.line);
        }
      } catch (propertiesErr) {
        console.warn('WIP details fetch failed (non-blocking):', propertiesErr.message);
        
        if (propertiesErr?.message?.includes('403') || propertiesErr?.message?.includes('401')) {
          console.error('📝 Authorization issue. Possible fixes:');
          console.error('   1. Ensure @frappe.whitelist() decorator is added to your Python function');
          console.error('   2. Check that the user has proper permissions');
          console.error('   3. Verify the API method path is correct');
        }
      }

      console.log('✓ Login successful! Reloading app...');
      window.location.reload();
      
    } catch (err) {
      console.error('Login error:', err);
      
      let errorMessage = 'Login failed. Please try again.';
      
      if (err?.message) {
        if (err.message.includes('Incomplete login details')) {
          errorMessage = 'Please enter valid username and password.';
        } else if (err.message.includes('ERR_CONNECTION_REFUSED') || err.message.includes('Failed to fetch')) {
          errorMessage = 'Cannot connect to server. Please check your internet connection.';
        } else if (err.message.includes('401') || err.message.includes('Incorrect password')) {
          errorMessage = 'Invalid username or password. Please try again.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setTimeout(() => {
        setLoading(false);
        setIsLoading(false);
      }, 1000);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img src="/pragyaa-logo.png" alt="Pragyaa Logo" className="header-logo" />
          <div className="header-text">
            <h1 className="login-title">Welcome Back</h1>
            <p className="login-subtitle">Sign in to your account</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="error-message">
              <svg className="error-icon" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="username" className="form-label">
              Username or Email
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={credentials.username}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter your username or email"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={credentials.password}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter your password"
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`login-button ${isLoading ? 'loading' : ''}`}
          >
            {isLoading ? (
              <>
                <svg className="loading-spinner" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;